import { commentToId } from "../generation/user_data/comment.generator";
import {
  QuizCompletedEvent,
  UserCommentEvent,
} from "../interaction_tracking/experiment_event";
import { SocialMediaDriver } from "../interaction_tracking/network";
import { Author } from "../manifest/models/author.models";
import { ReactiveController } from "../utils/controller";
import { DateFormatter } from "../utils/date_formatter";
import { getUsernameFor } from "../utils/username";
import {
  PostComment,
  PostData,
  PostEvent,
  PostStateMixin,
} from "./post.models";

export class PostController<T extends PostData> extends ReactiveController<
  T & PostStateMixin
> {
  private originalLikeCount: number;
  private originalDislikeCount: number;
  private originalShareCount: number;
  private driver: SocialMediaDriver;
  private testId: string;
  private user: Author;
  private dateFormatter = new DateFormatter();
  private followupTimerActive: boolean = false;

  private _onRepliesClicked?: PostEvent;
  private _onLikeClicked?: PostEvent;
  private _onDislikeClicked?: PostEvent;
  private _onShareClicked?: PostEvent;
  private _onQuizCompleted?: PostEvent;

  constructor(
    post: T,
    driver: SocialMediaDriver,
    testId: string,
    user?: Author,
  ) {
    super({
      ...post,
      userShared: false,
      displayShareSnackbar: false,
      userLiked: false,
      userDisliked: false,
      viewingReplies: false,
      displayReplyFollowup: false,
    });
    this.driver = driver;

    if (user == null) {
      throw new Error(`No user provided for post controller for ${post.id}`);
    }

    this.user = user;
    this.originalLikeCount = post.likes ?? 0;
    this.originalDislikeCount = post.dislikes ?? 0;
    this.originalShareCount = post.shares ?? 0;
    this.testId = testId;
  }

  onRepliesClicked() {
    this.setState({
      ...this.state,
      viewingReplies: true,
    });
    if (this._onRepliesClicked != null) this._onRepliesClicked(this.state);
  }

  onRepliesClosed() {
    this.setState({
      ...this.state,
      viewingReplies: false,
    });
  }

  onLikeClicked() {
    this.setState({
      ...this.state,
      likes: this.state.userLiked
        ? this.originalLikeCount
        : this.originalLikeCount + 1,
      userLiked: !this.state.userLiked,
    });

    if (this._onLikeClicked != null) this._onLikeClicked(this.state);
  }

  onDislikeClicked() {
    this.setState({
      ...this.state,
      dislikes: this.state.userDisliked
        ? this.originalDislikeCount
        : this.originalDislikeCount + 1,
      userDisliked: !this.state.userDisliked,
    });

    if (this._onDislikeClicked != null) this._onDislikeClicked(this.state);
  }

  onShareClicked() {
    if (this.state.userShared) return;

    this.setState({
      ...this.state,
      userShared: true,
      displayShareSnackbar: true,
      shares: this.originalShareCount + 1,
    });
    if (this._onShareClicked != null) this._onShareClicked(this.state);
  }

  onShareDismissed() {
    if (!this.state.userShared) return;

    this.setState({
      ...this.state,
      displayShareSnackbar: false,
    });
  }

  onCommentSubmitted(comment: string) {
    const newComment: PostComment = {
      id: commentToId(comment),
      body: comment,
      date: this.dateFormatter.formatOffsetAsDate(0),
      avatar: this.user.avatar.filename,
      author: `${getUsernameFor(this.user)}`,
    };
    this.setState({
      ...this.state,
      comments: [...(this.state.comments ?? []), newComment],
    });
    this._startFollowupTimer();

    if (this.testId == null) return;
    this.driver?.putExperimentEventRecord(
      new UserCommentEvent(new Date(), comment),
      this.testId,
    );
  }

  _startFollowupTimer() {
    if (this.followupTimerActive) return;
    if (this.state.userFollowup == null) return;
    this.followupTimerActive = true;
    const delay = this.state.userFollowup.delay;
    console.debug("scheduling followup timer for ", delay, "seconds");
    setTimeout(() => {
      console.debug("showing snackbar");
      this.setState({
        ...this.state,
        displayReplyFollowup: true,
      });
    }, delay * 1000);
  }

  onReplyFollowupDismissed() {
    if (!this.state.displayReplyFollowup) return;
    this.followupTimerActive = false;

    this.setState({
      ...this.state,
      displayReplyFollowup: false,
    });
  }

  onQuizCompleted(responses: any) {
    if (this._onQuizCompleted != null) {
      this._onQuizCompleted(this.state);
    }

    this.driver.putExperimentEventRecord(
      new QuizCompletedEvent(new Date(), responses),
      this.testId,
    );
  }
}
